import { useLicense } from "@coach/license-client";
import type { LocalDb } from "@coach/sync";
import {
  createSyncEngine,
  type SyncEngine,
  type SyncStatus,
} from "@coach/sync";
import { tauriLocalDb } from "@coach/sync/adapters/tauri";
import { webLocalDb } from "@coach/sync/adapters/web";
// Loaded as a string by Vite's `?raw` import.
// biome-ignore lint/correctness/noUnusedImports: type-only
import schemaSql from "@coach/sync/schema.sql?raw";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TEAM_LIMIT_REACHED_EVENT } from "../components/team-limit-notice.js";
import { api } from "../lib/api";
import { useActiveTeamId } from "../lib/use-active-org.js";
import { listTeams, pruneOrphanedTeams } from "../repos/teams";

interface SyncContextValue {
  db: LocalDb | null;
  engine: SyncEngine | null;
  status: SyncStatus;
  clientId: string | null;
  /** Convenience: id of the user's organization (from license claims). */
  orgId: string | null;
  /** Better-Auth user id of the signed-in coach. */
  userId: string | null;
  /** First team the user has access to (active team falls back to this). */
  defaultTeamId: string | null;
}

const SyncContext = createContext<SyncContextValue>({
  db: null,
  engine: null,
  status: { kind: "idle", lastSyncedAt: null, pending: 0 },
  clientId: null,
  orgId: null,
  userId: null,
  defaultTeamId: null,
});

const CLIENT_ID_KEY = "coach.clientId";
const DB_NAME = "coach.db";
/**
 * Bump whenever `packages/sync/src/schema.sql` makes a backwards-incompatible
 * change (renamed/removed columns, FK shape changes). On mismatch we DROP
 * every domain table so the local mirror can be rebuilt cleanly from the
 * server. The outbox is dropped too — anything in-flight at the moment of
 * the schema change is unsalvageable.
 */
const SCHEMA_VERSION = "3";
const SCHEMA_VERSION_KEY = "schema_version";
/**
 * Tracks the orgId the local mirror was last hydrated against. When the
 * coach switches the active org (see `switchActiveOrganization`) the
 * sync engine re-bootstraps with a new orgId; if we don't wipe the
 * previous tenant's rows + outbox entries here, we'd push stale ops
 * scoped to the wrong org against the new tenant (→ FK violations,
 * cross-tenant data leakage in the UI). Storing the marker inside
 * `sync_meta_kv` keeps it durable across reloads without requiring a
 * separate localStorage roundtrip on every boot.
 */
const LAST_ORG_KEY = "last_org_id";

function getOrCreateClientId(): string {
  let id = localStorage.getItem(CLIENT_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(CLIENT_ID_KEY, id);
  }
  return id;
}

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/**
 * Bootstraps the local SQLite store + sync engine after a valid license is
 * present. We keep this off the boot path until sign-in succeeds so the
 * webview doesn't open a DB the user might never use.
 */
export function SyncProvider({ children }: { children: ReactNode }) {
  const { state, claims } = useLicense();
  const qc = useQueryClient();
  const [db, setDb] = useState<LocalDb | null>(null);
  const [engine, setEngine] = useState<SyncEngine | null>(null);
  const [status, setStatus] = useState<SyncStatus>({
    kind: "idle",
    lastSyncedAt: null,
    pending: 0,
  });
  const [defaultTeamId, setDefaultTeamId] = useState<string | null>(null);
  // User's chosen active team (server-resolved via /me). When set, it wins
  // over the bootstrapped fallback so team-scoped queries (players, …)
  // reflect the team the coach actually picked in the team switcher.
  const activeTeamId = useActiveTeamId();
  const effectiveTeamId = activeTeamId ?? defaultTeamId;
  const clientId = useMemo(() => getOrCreateClientId(), []);
  const startedRef = useRef(false);
  const orgId = claims?.orgId ?? null;
  const userId = claims?.sub ?? null;

  useEffect(() => {
    if (state.kind !== "valid" && state.kind !== "grace") return;
    if (!orgId || !userId) return;
    if (startedRef.current) return;
    startedRef.current = true;

    let canceled = false;
    let createdEngine: SyncEngine | null = null;
    let openedDb: LocalDb | null = null;

    (async () => {
      const factory = isTauri() ? tauriLocalDb : webLocalDb;
      const localDb = await factory.open(DB_NAME);

      // ── Schema version check ───────────────────────────────────────
      // The sync_meta_kv table is created BEFORE the version probe so
      // older installs (which never wrote a version) get detected on
      // the very next boot.
      await localDb.exec(
        `CREATE TABLE IF NOT EXISTS sync_meta_kv (
           key   TEXT PRIMARY KEY,
           value TEXT NOT NULL
         )`,
      );
      const prior = (
        await localDb.query<{ value: string }>(
          `SELECT value FROM sync_meta_kv WHERE key = ?`,
          [SCHEMA_VERSION_KEY],
        )
      )[0]?.value;
      if (prior !== SCHEMA_VERSION) {
        // Wipe every domain + sync table — the next pull will rebuild
        // them from the server.
        const dropTables = [
          "team",
          "team_coach",
          "coach",
          "player",
          "calendar_event",
          "formation",
          "board",
          "media_ref",
          "local_media",
          "sync_outbox",
          "sync_cursor",
        ];
        for (const t of dropTables) {
          try {
            await localDb.exec(`DROP TABLE IF EXISTS ${t}`);
          } catch {
            /* ignore — table may not exist */
          }
        }
        await localDb.exec(
          `INSERT INTO sync_meta_kv(key, value) VALUES(?, ?)
             ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
          [SCHEMA_VERSION_KEY, SCHEMA_VERSION],
        );
      }

      // ── Org-change detection ───────────────────────────────────────
      // If the last bootstrap was against a different org (coach
      // switched clubs via /select/org), wipe every domain row + queued
      // outbox op. Otherwise we'd push the previous tenant's pending
      // writes against the new tenant — Prisma rejects them with FK
      // violations (e.g. `team_coach_teamId_fkey`) and the outbox
      // would otherwise retry forever. We deliberately keep the schema
      // tables in place (DELETE, not DROP) so the schema.sql apply
      // below stays cheap and idempotent.
      const lastOrgId = (
        await localDb.query<{ value: string }>(
          `SELECT value FROM sync_meta_kv WHERE key = ?`,
          [LAST_ORG_KEY],
        )
      )[0]?.value;
      if (lastOrgId && lastOrgId !== orgId) {
        const wipeTables = [
          "team_coach",
          "team",
          "coach",
          "player",
          "calendar_event",
          "formation",
          "board",
          "media_ref",
          "local_media",
          "sync_outbox",
          "sync_cursor",
        ];
        for (const t of wipeTables) {
          try {
            await localDb.exec(`DELETE FROM ${t}`);
          } catch {
            /* table may not exist on a brand-new install — fine */
          }
        }
        console.info(
          `[sync] org changed (${lastOrgId} → ${orgId}); local mirror wiped`,
        );
      }
      await localDb.exec(
        `INSERT INTO sync_meta_kv(key, value) VALUES(?, ?)
           ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
        [LAST_ORG_KEY, orgId],
      );

      // Run schema once — every CREATE TABLE is `IF NOT EXISTS`.
      // Strip `-- …` line comments first so they don't get glued to the
      // following statement and falsely trip the `startsWith('--')` filter.
      const statements = (schemaSql as unknown as string)
        .split(/\r?\n/)
        .map((line) => line.replace(/--.*$/, ""))
        .join("\n")
        .split(/;\s*(?:\r?\n|$)/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      for (const stmt of statements) {
        try {
          await localDb.exec(stmt);
        } catch (err) {
          // Idempotent migrations: an `ALTER TABLE … ADD COLUMN` is the
          // only way SQLite lets us evolve an existing table, but the
          // statement is not itself idempotent. Swallow the duplicate-
          // column error so reruns on a freshly-created DB stay green.
          const msg = err instanceof Error ? err.message : String(err);
          if (/duplicate column name/i.test(msg)) continue;
          throw err;
        }
      }
      if (canceled) {
        await localDb.close();
        return;
      }
      openedDb = localDb;

      const transport = api().sync;
      const eng = createSyncEngine({
        db: localDb,
        transport,
        clientId,
        onStatus: setStatus,
        onConflicts: (conflicts) => {
          // The server dropped a new team because the license's team cap is
          // reached — tell the coach instead of letting it silently vanish.
          if (conflicts.some((c) => c.message === "team_limit_reached")) {
            window.dispatchEvent(new CustomEvent(TEAM_LIMIT_REACHED_EVENT));
          }
        },
        onApplied: (entities) => {
          // Pull applied new rows → refresh every query whose key starts with
          // the entity name. Repos use ['players', orgId, teamId] etc.
          for (const entity of entities) {
            qc.invalidateQueries({ queryKey: [entityKey(entity)] });
          }
        },
      });
      createdEngine = eng;
      setDb(localDb);
      setEngine(eng);
      eng.start();

      // Best-effort initial pull to populate local store from any prior device.
      try {
        await eng.syncNow();
      } catch {
        /* offline is fine */
      }

      // Drop legacy "Mein Team" / "Dein Team" placeholders and any other
      // client-only team rows that never made it to the server. This
      // keeps the team switcher from showing phantoms after an org
      // change. We intentionally do NOT auto-create a default team
      // here — the user picks (or creates) one via the team selector,
      // which makes the active team a deliberate choice, not a side
      // effect of bootstrap.
      try {
        const pruned = await pruneOrphanedTeams(localDb, orgId);
        if (pruned > 0) {
          console.info(
            `[sync] pruned ${pruned} orphan team row(s) in org ${orgId}`,
          );
        }
        const teams = await listTeams(localDb, orgId);
        setDefaultTeamId(teams[0]?.id ?? null);
        if (pruned > 0) {
          qc.invalidateQueries({ queryKey: ["teams"] });
        }
      } catch (err) {
        console.warn("[sync] team bootstrap failed", err);
      }
    })().catch((err) => {
      console.error("[sync] bootstrap failed", err);
    });

    return () => {
      canceled = true;
      createdEngine?.stop();
      void openedDb?.close();
      startedRef.current = false;
    };
  }, [state.kind, clientId, orgId, userId]);

  const value: SyncContextValue = {
    db,
    engine,
    status,
    clientId,
    orgId,
    userId,
    defaultTeamId: effectiveTeamId,
  };
  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}

export function useSync(): SyncContextValue {
  return useContext(SyncContext);
}

/** Map sync entity name → React-Query key prefix used by repos. */
function entityKey(entity: string): string {
  switch (entity) {
    case "team":
      return "teams";
    case "team_coach":
      return "team_coaches";
    case "player":
      return "players";
    case "calendar_event":
      return "events";
    case "formation":
      return "formations";
    case "board":
      return "boards";
    case "media_ref":
      return "media";
    default:
      return entity;
  }
}
